import { router } from "@inertiajs/react"
import { Badge } from "@/components/ui/badge"
import { TicketStatusBadge } from "@/components/domain/ticket-status-badge"
import { TicketPriorityBadge } from "@/components/domain/ticket-priority-badge"
import { RowAvatar } from "@/components/domain/row-avatar"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { fmtRelativeTime } from "@/lib/format"
import { COLUMNS, type ColumnDef, type ColumnKey } from "./columns"
import { SlaPill } from "./sla-pill"
import type { Ticket, TicketSla } from "@/types/ticket"

// Tooltip content for the "SLA" column header. Explains the abbreviation
// itself plus the three pill labels (FR / Idle / Res) so the compact
// column doesn't depend on tribal knowledge.
const SLA_HEADER_HELP = (
  <div className="space-y-2">
    <div>
      <strong>SLA</strong> = Service Level Agreement. Time targets your team
      commits to once a ticket is escalated to a human.
    </div>
    <ul className="space-y-1 pl-3 list-disc">
      <li><strong>FR</strong> — first-response. Escalation → first operator reply.</li>
      <li><strong>Idle</strong> — rolling reply clock. Resets on each customer
        message; clears when an operator replies. <em>(×N)</em> shows total
        missed cycles for the ticket.</li>
      <li><strong>Res</strong> — resolution. Escalation → ticket closed.</li>
    </ul>
    <div className="text-app-muted">
      Targets are configured per escalation policy, with a company default
      fallback. <em>—</em> means no SLA was configured for that clock.
    </div>
  </div>
)

type Props = {
  tickets: Ticket[]
  visibleColumns: ColumnKey[]
  sortBy: string | null
  sortDir: "asc" | "desc"
  onSortChange: (sortBy: string | null, dir: "asc" | "desc") => void
}

export function TicketsTable({
  tickets,
  visibleColumns,
  sortBy,
  sortDir,
  onSortChange,
}: Props) {
  const cols = COLUMNS.filter((c) => visibleColumns.includes(c.key))

  if (tickets.length === 0) {
    return (
      <div className="rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
        No tickets match these filters.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-card border border-border bg-app-panel">
      <table className="w-full table-auto border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-white/[0.02] text-left">
            {cols.map((c) => (
              <th
                key={c.key}
                className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-app-muted"
              >
                <span className="inline-flex items-center gap-1">
                  {c.sortKey ? (
                    <button
                      type="button"
                      onClick={() => {
                        const isActive = sortBy === c.sortKey
                        const nextDir = isActive && sortDir === "desc" ? "asc" : "desc"
                        onSortChange(c.sortKey, nextDir)
                      }}
                      className="inline-flex items-center gap-1 hover:text-app-text"
                    >
                      {c.label}
                      {sortBy === c.sortKey && (
                        <span className="text-[10px]">{sortDir === "asc" ? "↑" : "↓"}</span>
                      )}
                    </button>
                  ) : (
                    <span>{c.label}</span>
                  )}
                  {c.key === "sla" && <InfoTooltip side="bottom">{SLA_HEADER_HELP}</InfoTooltip>}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr
              key={t.id}
              onClick={() => router.visit(`/tickets/${t.id}`)}
              className="cursor-pointer border-b border-border/50 align-top transition hover:bg-white/[0.04]"
            >
              {cols.map((c) => (
                <td key={c.key} className="px-4 py-3">
                  {renderCell(c, t)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function renderCell(col: ColumnDef, t: Ticket) {
  switch (col.key) {
    case "ticket_id":
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-app-text">{ticketNumberLabel(t)}</span>
        </div>
      )
    case "subject":
      return (
        <div className="line-clamp-2 max-w-[320px] text-app-text">
          {t.subject}
        </div>
      )
    case "requester": {
      const r = t.requester
      return (
        <div className="space-y-0.5">
          <div className="font-medium text-app-text">{r.name || "—"}</div>
          {r.email && <div className="text-xs text-app-muted">{r.email}</div>}
          {r.sourceKey && (
            <div className="font-mono text-[11px] text-app-subtle">User ID: {r.sourceKey}</div>
          )}
        </div>
      )
    }
    case "queue":
      return t.ticketQueue ? <Badge tone="neutral">{t.ticketQueue.name}</Badge> : <Muted />
    case "priority":
      return <TicketPriorityBadge priority={t.priority} />
    case "status":
      return <TicketStatusBadge status={t.status} />
    case "assignee":
      return t.humanAssignee ? (
        <div className="flex items-center gap-2">
          <RowAvatar
            name={t.humanAssignee.name || t.humanAssignee.email}
            src={t.humanAssignee.avatarUrl}
            className="h-6 w-6 text-[10px]"
          />
          <span className="text-app-text">{t.humanAssignee.name || t.humanAssignee.email}</span>
        </div>
      ) : (
        <Muted />
      )
    case "created_at":
      return <CreatedAtCell iso={t.createdAt} />
    case "escalated_at":
      return <RelativeTimeCell iso={t.escalatedAt} />
    case "last_response":
      return <RelativeTimeCell iso={t.lastMessageAt} />
    case "sla":
      return <SlaCell sla={t.sla} />
  }
}

function ticketNumberLabel(t: Ticket) {
  if (t.displayTicketNumber) return t.displayTicketNumber
  if (t.supportProvider === "zendesk" && t.externalId) return `ZD-${t.externalId}`
  return t.displayTicketNumber || (t.ticketNumber != null ? `TK-${t.ticketNumber}` : "—")
}

function RelativeTimeCell({ iso }: { iso: string | null | undefined }) {
  if (!iso) return <Muted />
  return (
    <span className="whitespace-nowrap text-xs text-app-muted" title={iso}>
      {fmtRelativeTime(iso)}
    </span>
  )
}

function CreatedAtCell({ iso }: { iso: string }) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return <Muted />
  const absolute = d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  })
  return (
    <div className="space-y-0.5">
      <div className="text-app-text">{absolute}</div>
      <div className="text-xs text-app-muted">{fmtRelativeTime(iso)}</div>
    </div>
  )
}

// Three compact rows — one per SLA clock. Renders "—" when nothing is
// tracked across all three (happens for convos created before any policy
// existed for the agent). Each row shows label / colored dot / caption,
// driven by AgentConvo#sla_*_state on the server.
function SlaCell({ sla }: { sla: TicketSla }) {
  const allUntracked =
    sla.firstResponse.state === "not_tracked" &&
    sla.idleResponse.state === "not_tracked" &&
    sla.resolution.state === "not_tracked"
  if (allUntracked) return <Muted />
  return (
    <div className="space-y-0.5">
      <SlaPill
        label="FR"
        state={sla.firstResponse.state}
        dueAt={sla.firstResponse.dueAt}
        metAt={sla.firstResponse.metAt}
        breachedAt={sla.firstResponse.breachedAt}
      />
      <SlaPill
        label="Idle"
        state={sla.idleResponse.state}
        dueAt={sla.idleResponse.dueAt}
        breachedAt={sla.idleResponse.firstBreachedAt}
        breachCount={sla.idleResponse.breachCount}
      />
      <SlaPill
        label="Res"
        state={sla.resolution.state}
        dueAt={sla.resolution.dueAt}
        breachedAt={sla.resolution.breachedAt}
      />
    </div>
  )
}

function Muted() {
  return <span className="text-app-subtle">—</span>
}
